import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Pencil, Trash2, Plus, EyeOff, ChevronUp, ChevronDown, Shield } from 'lucide-react';

function renderContent(text) {
  if (!text) return null;
  // Soporte muy ligero de markdown:
  // - lineas que empiezan por "* " => bullets
  // - parrafos separados por linea en blanco
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    const isBulletBlock = lines.every(l => l.trim().startsWith('* '));
    if (isBulletBlock) {
      return (
        <ul key={bi} className="list-disc pl-5 space-y-1.5 my-2">
          {lines.map((l, li) => (
            <li key={li} className="text-gray-200 leading-relaxed text-[15px]">{l.trim().replace(/^\*\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={bi} className="text-gray-200 leading-relaxed text-[15px] my-2 whitespace-pre-line">{block}</p>
    );
  });
}

export default function BuenasPracticasTab({ isEntrenador }) {
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // seccion en edicion
  const [creating, setCreating] = useState(false);

  const fetchSecciones = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/normas`);
      setSecciones(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSecciones(); }, [fetchSecciones]);

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.titulo?.trim()) {
      alert('El título es obligatorio');
      return;
    }
    try {
      if (creating) {
        await axios.post(`${API}/normas`, {
          icono: editing.icono || '',
          titulo: editing.titulo.trim(),
          contenido: editing.contenido || '',
          orden: editing.orden ?? (secciones.length + 1),
          visible: editing.visible !== false
        });
      } else {
        await axios.put(`${API}/normas/${editing.id}`, {
          icono: editing.icono,
          titulo: editing.titulo,
          contenido: editing.contenido,
          orden: editing.orden,
          visible: editing.visible
        });
      }
      setEditing(null);
      setCreating(false);
      await fetchSecciones();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sección? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/normas/${id}`);
      await fetchSecciones();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleMove = async (seccion, direction) => {
    const sorted = [...secciones].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex(s => s.id === seccion.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      await Promise.all([
        axios.put(`${API}/normas/${seccion.id}`, { orden: other.orden }),
        axios.put(`${API}/normas/${other.id}`, { orden: seccion.orden })
      ]);
      await fetchSecciones();
    } catch (e) {
      alert('Error al reordenar');
    }
  };

  const openCreate = () => {
    setCreating(true);
    setEditing({
      icono: '⚔️',
      titulo: '',
      contenido: '',
      orden: secciones.length + 1,
      visible: true
    });
  };

  return (
    <div data-testid="buenas-practicas-tab" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gold flex items-center gap-2">
            <Shield className="h-6 w-6" /> Buenas Prácticas
          </h2>
          <p className="text-gray-400 text-sm">El código del Klan · Cómo nos comportamos los lobos de MK</p>
        </div>
        {isEntrenador && (
          <Button
            onClick={openCreate}
            className="btn-primary-mk"
            data-testid="btn-add-norma"
          >
            <Plus className="mr-2 h-4 w-4" /> Nueva sección
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-12">Cargando…</p>
      ) : secciones.length === 0 ? (
        <p className="text-gray-500 text-center py-12">Sin secciones todavía.</p>
      ) : (
        <div className="space-y-4">
          {secciones.map((s, idx) => (
            <div
              key={s.id}
              data-testid={`norma-${s.id}`}
              className={`relative bg-gradient-to-br from-gray-900/60 to-gray-900/30 border rounded-xl p-5 ${s.visible === false ? 'border-gray-700 opacity-60' : 'border-gold/30'}`}
            >
              {/* Header de la seccion */}
              <div className="flex items-start gap-3 mb-2">
                {s.icono && <div className="text-3xl leading-none flex-shrink-0">{s.icono}</div>}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gold flex items-center gap-2 flex-wrap">
                    {s.titulo}
                    {s.visible === false && (
                      <span className="text-[10px] uppercase tracking-wider bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">
                        <EyeOff className="h-3 w-3 inline mr-0.5" /> Oculta
                      </span>
                    )}
                  </h3>
                </div>
                {isEntrenador && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMove(s, 'up')}
                      disabled={idx === 0}
                      className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Subir"
                      data-testid={`btn-up-${s.id}`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleMove(s, 'down')}
                      disabled={idx === secciones.length - 1}
                      className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Bajar"
                      data-testid={`btn-down-${s.id}`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { setCreating(false); setEditing({ ...s }); }}
                      className="p-1.5 rounded hover:bg-blue-900/40 text-gray-400 hover:text-blue-300"
                      title="Editar"
                      data-testid={`btn-edit-${s.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(s.id)}
                      className="p-1.5 rounded hover:bg-red-900/40 text-gray-400 hover:text-red-300"
                      title="Eliminar"
                      data-testid={`btn-delete-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="pl-1">{renderContent(s.contenido)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Footer "no rompas el código" */}
      <div className="mt-6 text-center py-6 px-4 border-t border-gold/20">
        <p className="text-gold text-sm font-semibold tracking-wider uppercase">
          Honor · Disciplina · Hermandad
        </p>
        <p className="text-gray-500 text-xs mt-1">El que rompe el código, rompe al Klan.</p>
      </div>

      {/* Modal editar/crear */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setCreating(false); } }}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gold/30 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gold flex items-center gap-2">
              {creating ? 'Nueva sección' : 'Editar sección'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-[100px_1fr] gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Icono</label>
                  <Input
                    value={editing.icono || ''}
                    onChange={(e) => setEditing({ ...editing, icono: e.target.value })}
                    placeholder="⚔️"
                    className="bg-gray-950 border-gray-700 text-white text-lg"
                    data-testid="input-icono"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Título</label>
                  <Input
                    value={editing.titulo || ''}
                    onChange={(e) => setEditing({ ...editing, titulo: e.target.value })}
                    placeholder="Respeto ante todo"
                    className="bg-gray-950 border-gray-700 text-white"
                    data-testid="input-titulo"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Contenido</label>
                <Textarea
                  value={editing.contenido || ''}
                  onChange={(e) => setEditing({ ...editing, contenido: e.target.value })}
                  rows={10}
                  placeholder={"Texto libre.\n\nUsa una línea en blanco para separar párrafos.\nPara hacer bullets, empieza la línea con \"* \"."}
                  className="bg-gray-950 border-gray-700 text-white font-mono text-sm"
                  data-testid="input-contenido"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  💡 Usa <code className="bg-gray-800 px-1 rounded">* </code> al principio de la línea para crear bullets.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Orden</label>
                  <Input
                    type="number"
                    value={editing.orden ?? 0}
                    onChange={(e) => setEditing({ ...editing, orden: parseInt(e.target.value || '0', 10) })}
                    className="bg-gray-950 border-gray-700 text-white"
                    data-testid="input-orden"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editing.visible !== false}
                      onChange={(e) => setEditing({ ...editing, visible: e.target.checked })}
                      className="w-4 h-4 accent-gold"
                      data-testid="input-visible"
                    />
                    Visible para los miembros
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setEditing(null); setCreating(false); }}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  data-testid="btn-cancel-norma"
                >
                  Cancelar
                </Button>
                <Button onClick={handleSave} className="btn-primary-mk" data-testid="btn-save-norma">
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
